/* ============================================================
   PintaFlow — Design Direction V3: "Aperture" (Apple Minimal)
   ----------------------------------------------------------
   - Color: Black + Soft Off-white + a single Electric Blue dot
   - Feel: Apple keynote × Linear changelog × museum wall card
   - Type: Geist (or Söhne-feel via system) + tight tracking
   - Hero: One enormous Pipeline diagram, dead-center, museum
   ============================================================ */
import { Plus } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const STAGES = ["Idea", "Research", "Script", "Create", "Schedule", "Published"];

export default function LandingV3() {
  return (
    <div
      className="min-h-screen bg-[#FAFAF7] text-[#0A0A0A] overflow-x-hidden"
      style={{ fontFamily: "'Geist', 'SF Pro Display', system-ui, sans-serif" }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=Geist+Mono:wght@400;500&display=swap"
        rel="stylesheet"
      />

      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#FAFAF7]/85 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-[1180px] mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="relative w-6 h-6 rounded-lg bg-black flex items-center justify-center">
              <div className="w-1.5 h-1.5 rounded-full bg-[#0066FF]" />
            </div>
            <span className="text-[15px] font-semibold tracking-tight">PintaFlow</span>
          </div>
          <button
            onClick={startGoogleLogin}
            className="text-[13px] bg-black text-white px-3.5 py-1.5 rounded-full hover:bg-[#0066FF] transition-colors"
          >
            Sign in
          </button>
        </div>
      </header>

      {/* Hero block */}
      <section className="max-w-[1180px] mx-auto px-6 pt-20 md:pt-32 pb-14 text-center">
        <div
          className="text-[11px] uppercase tracking-[0.28em] text-black/45 mb-6"
          style={{ fontFamily: "'Geist Mono', ui-monospace, monospace" }}
        >
          The Creator Operating System
        </div>
        <h1 className="text-[52px] sm:text-[88px] md:text-[120px] font-semibold tracking-[-0.045em] leading-[0.95] max-w-5xl mx-auto">
          One surface.<br />
          <span className="text-black/30">From idea</span><br />
          to <span className="relative inline-block">
            published<span className="inline-block w-3 h-3 rounded-full bg-[#0066FF] align-top ml-2 -translate-y-2" />
          </span>
        </h1>
        <p className="mt-8 text-lg md:text-xl text-black/55 max-w-xl mx-auto leading-relaxed">
          PintaFlow is the operating system creators run their work on — calm, fast, made for the craft.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={startGoogleLogin}
            className="bg-black text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[#0066FF] transition-colors"
          >
            Continue with Google
          </button>
          <span className="text-[12px] text-black/40">No card. No noise. Free in beta.</span>
        </div>
      </section>

      {/* PIPELINE — museum centerpiece */}
      <section className="max-w-[1180px] mx-auto px-6 pb-24 md:pb-32">
        <div className="rounded-[28px] bg-[#0A0A0A] text-white p-7 md:p-14 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 30% 20%, #0066FF 0%, transparent 40%), radial-gradient(circle at 80% 80%, #ffffff 0%, transparent 50%)",
            }}
          />
          <div className="relative">
            <div className="flex items-center justify-between mb-10 flex-wrap gap-3">
              <div>
                <div
                  className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2"
                  style={{ fontFamily: "'Geist Mono', monospace" }}
                >
                  ★ The pipeline
                </div>
                <h2 className="text-2xl md:text-4xl font-semibold tracking-tight">
                  Six stages. Every format. <span className="text-white/40">Zero chaos.</span>
                </h2>
              </div>
              <div
                className="text-xs text-white/40"
                style={{ fontFamily: "'Geist Mono', monospace" }}
              >
                YT · Shorts · Reels · X · Blog
              </div>
            </div>

            {/* Stage track */}
            <div className="relative">
              <div className="absolute top-[18px] left-[28px] right-[28px] h-px bg-white/15" />
              <div className="grid grid-cols-6 relative">
                {STAGES.map((s, i) => (
                  <div key={s} className="flex flex-col items-center">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${
                        i === STAGES.length - 1
                          ? "bg-[#0066FF] text-white shadow-[0_0_24px_rgba(0,102,255,0.5)]"
                          : "bg-white text-black"
                      }`}
                    >
                      {i + 1}
                    </div>
                    <div className="mt-3 text-[12px] sm:text-sm font-medium tracking-tight">
                      {s}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sample cards */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { t: "Why I quit FAANG", p: "YT Long", s: "Script" },
                { t: "Editing for retention", p: "Reels", s: "Create" },
                { t: "5 tools I shipped with", p: "Tweet", s: "Published" },
                { t: "Hook test #14", p: "Reels", s: "Research" },
                { t: "Drop Tuesday 8 AM", p: "Blog", s: "Schedule" },
                { t: "Newsletter v3", p: "Blog", s: "Idea" },
              ].map((c, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-4 hover:border-[#0066FF]/60 hover:bg-[#0066FF]/[0.08] transition group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] text-white/50"
                      style={{ fontFamily: "'Geist Mono', monospace" }}
                    >
                      {c.p}
                    </span>
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] text-[#0066FF]"
                      style={{ fontFamily: "'Geist Mono', monospace" }}
                    >
                      → {c.s}
                    </span>
                  </div>
                  <div className="text-[15px] font-medium leading-snug">{c.t}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex items-center justify-center">
              <button className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white border border-white/15 rounded-full px-4 py-2">
                <Plus size={13} /> Add an idea
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Big tile features (Apple way) */}
      <section className="max-w-[1180px] mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-[24px] bg-white border border-black/5 p-10 md:col-span-2">
            <div
              className="text-[10px] uppercase tracking-[0.28em] text-[#0066FF] mb-3"
              style={{ fontFamily: "'Geist Mono', monospace" }}
            >
              Streak · Consistency
            </div>
            <h3 className="text-3xl md:text-5xl font-semibold tracking-tight max-w-2xl">
              Show up. Quietly. Daily. <span className="text-black/30">PintaFlow keeps the count.</span>
            </h3>
            <div className="flex gap-1 mt-10">
              {Array.from({ length: 60 }).map((_, i) => {
                const intensity = [0, 1, 2, 3, 4][Math.floor(Math.random() * 5)];
                const cls = ["bg-black/5", "bg-black/20", "bg-black/45", "bg-black/70", "bg-[#0066FF]"][intensity];
                return <div key={i} className={`w-3 h-3 sm:w-4 sm:h-4 rounded-sm ${cls}`} />;
              })}
            </div>
          </div>

          <div className="rounded-[24px] bg-white border border-black/5 p-8">
            <div
              className="text-[10px] uppercase tracking-[0.28em] text-black/40 mb-3"
              style={{ fontFamily: "'Geist Mono', monospace" }}
            >
              Swipe File
            </div>
            <h4 className="text-2xl font-semibold tracking-tight">References, indexed.</h4>
            <p className="text-sm text-black/55 mt-2 leading-relaxed">
              Paste a URL — PintaFlow grabs the title and thumbnail. Upload an image — it&apos;s tagged and searchable.
            </p>
            <div className="mt-6 grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="aspect-square rounded-lg bg-gradient-to-br from-black/5 to-black/15"
                  style={{ filter: `hue-rotate(${i * 20}deg)` }}
                />
              ))}
            </div>
          </div>

          <div className="rounded-[24px] bg-black text-white p-8">
            <div
              className="text-[10px] uppercase tracking-[0.28em] text-[#0066FF] mb-3"
              style={{ fontFamily: "'Geist Mono', monospace" }}
            >
              Offline-first
            </div>
            <h4 className="text-2xl font-semibold tracking-tight">Captures don&apos;t need signal.</h4>
            <p className="text-sm text-white/55 mt-2 leading-relaxed">
              The PWA installs on home screens. Ideas saved offline sync the moment you&apos;re back.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 border border-white/15 rounded-full px-3 py-1.5 text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] animate-pulse" /> Synced just now
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-black/5 py-10">
        <div className="max-w-[1180px] mx-auto px-6 flex flex-wrap justify-between text-xs text-black/40">
          <span>© 2026 PintaFlow</span>
          <span style={{ fontFamily: "'Geist Mono', monospace" }}>direction · aperture · v3</span>
        </div>
      </footer>
    </div>
  );
}
